import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Chip,
  MenuItem,
  Select,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from "@mui/material";
import { feedbackApi } from "../services/api";

const getStatusColor = (status) => {
  switch (status) {
    case "resolved":
      return "success";
    case "read":
      return "info";
    case "new":
      return "warning";
    default:
      return "default";
  }
};

const getCategoryColor = (category) => {
  switch (category) {
    case "complaint":
      return "#ff6b6b";
    case "feedback":
      return "#4ecdc4";
    case "suggestion":
      return "#95e1d3";
    case "dealership_enquiry":
      return "#f59e0b";
    case "distributership_enquiry":
      return "#8b5cf6";
    case "other":
      return "#a8a8a8";
    default:
      return "#666";
  }
};

const getCategoryLabel = (category = "") => {
  const labels = {
    feedback: "Feedback",
    complaint: "Complaint",
    suggestion: "Suggestion",
    dealership_enquiry: "Dealership Enquiry",
    distributership_enquiry: "Distributership Enquiry",
    other: "Other"
  };

  return labels[category] || String(category).replaceAll("_", " ");
};

const AdminFeedback = () => {
  const [feedbacks, setFeedbacks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [updatingId, setUpdatingId] = useState(null);
  const [selectedFeedback, setSelectedFeedback] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);

  useEffect(() => {
    fetchFeedbacks();
  }, []);

  const fetchFeedbacks = async () => {
    try {
      setLoading(true);
      const response = await feedbackApi.getAll();
      setFeedbacks(response.data.feedbacks || []);
    } catch (err) {
      console.error("Fetch error:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async (id, status) => {
    try {
      setUpdatingId(id);
      await feedbackApi.updateStatus(id, status);

      // Update UI
      setFeedbacks((prev) =>
        prev.map((f) =>
          f._id === id ? { ...f, status } : f
        )
      );
    } catch (err) {
      console.error("Update failed:", err);
    } finally {
      setUpdatingId(null);
    }
  };

  const handleViewDetails = (feedback) => {
    setSelectedFeedback(feedback);
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedFeedback(null);
  };

  if (loading) {
    return (
      <Box textAlign="center" mt={5}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3, maxWidth: "1400px", margin: "0 auto" }}>
      {/* PAGE TITLE */}
      <Typography
        variant="h4"
        sx={{
          fontWeight: 700,
          mb: 3,
          fontSize: "1.75rem",
          color: "#C41E3A"
        }}
      >
        📋 Feedback & Complaints Management
      </Typography>

      {/* STATS */}
      <Box sx={{ display: "flex", gap: 2, mb: 3, flexWrap: "wrap" }}>
        <Paper sx={{ p: 2, flex: 1, minWidth: 150 }}>
          <Typography sx={{ fontSize: "0.85rem", color: "#666" }}>
            Total Submissions
          </Typography>
          <Typography sx={{ fontSize: "1.75rem", fontWeight: 700, color: "#333" }}>
            {feedbacks.length}
          </Typography>
        </Paper>
        <Paper sx={{ p: 2, flex: 1, minWidth: 150 }}>
          <Typography sx={{ fontSize: "0.85rem", color: "#666" }}>
            New
          </Typography>
          <Typography sx={{ fontSize: "1.75rem", fontWeight: 700, color: "#FF9800" }}>
            {feedbacks.filter((f) => f.status === "new").length}
          </Typography>
        </Paper>
        <Paper sx={{ p: 2, flex: 1, minWidth: 150 }}>
          <Typography sx={{ fontSize: "0.85rem", color: "#666" }}>
            Resolved
          </Typography>
          <Typography sx={{ fontSize: "1.75rem", fontWeight: 700, color: "#4CAF50" }}>
            {feedbacks.filter((f) => f.status === "resolved").length}
          </Typography>
        </Paper>
      </Box>

      {/* TABLE */}
      <TableContainer component={Paper} sx={{ overflowX: "auto" }}>
        <Table sx={{ minWidth: 1000 }}>
          {/* TABLE HEAD */}
          <TableHead sx={{ backgroundColor: "#f5f5f5" }}>
            <TableRow>
              <TableCell sx={{ fontSize: "0.9rem", fontWeight: 700, color: "#333" }}>
                Date
              </TableCell>
              <TableCell sx={{ fontSize: "0.9rem", fontWeight: 700, color: "#333" }}>
                Name
              </TableCell>
              <TableCell sx={{ fontSize: "0.9rem", fontWeight: 700, color: "#333" }}>
                Email
              </TableCell>
              <TableCell sx={{ fontSize: "0.9rem", fontWeight: 700, color: "#333" }}>
                Category
              </TableCell>
              <TableCell sx={{ fontSize: "0.9rem", fontWeight: 700, color: "#333" }}>
                Subject
              </TableCell>
              <TableCell sx={{ fontSize: "0.9rem", fontWeight: 700, color: "#333" }}>
                Status
              </TableCell>
              <TableCell sx={{ fontSize: "0.9rem", fontWeight: 700, color: "#333" }}>
                Actions
              </TableCell>
            </TableRow>
          </TableHead>

          {/* TABLE BODY */}
          <TableBody>
            {feedbacks.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ py: 3, fontSize: "0.95rem" }}>
                  No feedbacks yet
                </TableCell>
              </TableRow>
            ) : (
              feedbacks.map((feedback) => (
                <TableRow
                  key={feedback._id}
                  sx={{
                    "&:hover": { backgroundColor: "#f9f9f9" },
                    borderBottom: "1px solid #eee"
                  }}
                >
                  {/* Date */}
                  <TableCell sx={{ fontSize: "0.85rem", color: "#555" }}>
                    {new Date(feedback.createdAt).toLocaleDateString()}
                  </TableCell>

                  {/* Name */}
                  <TableCell sx={{ fontSize: "0.85rem", color: "#333", fontWeight: 500 }}>
                    {feedback.name}
                  </TableCell>

                  {/* Email */}
                  <TableCell sx={{ fontSize: "0.8rem", color: "#666" }}>
                    {feedback.email}
                  </TableCell>

                  {/* Category */}
                  <TableCell sx={{ fontSize: "0.85rem" }}>
                    <Chip
                      label={getCategoryLabel(feedback.category)}
                      size="small"
                      sx={{
                        backgroundColor: getCategoryColor(feedback.category),
                        color: "#fff",
                        fontWeight: 600,
                        fontSize: "0.75rem"
                      }}
                    />
                  </TableCell>

                  {/* Subject */}
                  <TableCell
                    sx={{
                      fontSize: "0.85rem",
                      color: "#555",
                      maxWidth: 200,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap"
                    }}
                  >
                    {feedback.subject || "—"}
                  </TableCell>

                  {/* Status */}
                  <TableCell sx={{ fontSize: "0.85rem" }}>
                    <Select
                      value={feedback.status}
                      onChange={(e) =>
                        handleStatusChange(feedback._id, e.target.value)
                      }
                      disabled={updatingId === feedback._id}
                      size="small"
                      sx={{
                        fontSize: "0.85rem",
                        "& .MuiOutlinedInput-input": {
                          py: 0.5,
                          px: 1
                        }
                      }}
                    >
                      <MenuItem value="new" sx={{ fontSize: "0.85rem" }}>
                        New
                      </MenuItem>
                      <MenuItem value="read" sx={{ fontSize: "0.85rem" }}>
                        Read
                      </MenuItem>
                      <MenuItem value="resolved" sx={{ fontSize: "0.85rem" }}>
                        Resolved
                      </MenuItem>
                    </Select>
                  </TableCell>

                  {/* Actions */}
                  <TableCell sx={{ fontSize: "0.85rem" }}>
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={() => handleViewDetails(feedback)}
                      sx={{
                        fontSize: "0.75rem",
                        py: 0.5,
                        px: 1,
                        textTransform: "none"
                      }}
                    >
                      View
                    </Button>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* DETAIL DIALOG */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontSize: "1.1rem", fontWeight: 700, color: "#C41E3A" }}>
          {selectedFeedback?.subject || "Feedback Details"}
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
            <Box>
              <Typography sx={{ fontSize: "0.8rem", color: "#999", fontWeight: 600 }}>
                NAME
              </Typography>
              <Typography sx={{ fontSize: "0.95rem", color: "#333" }}>
                {selectedFeedback?.name}
              </Typography>
            </Box>

            <Box>
              <Typography sx={{ fontSize: "0.8rem", color: "#999", fontWeight: 600 }}>
                EMAIL
              </Typography>
              <Typography sx={{ fontSize: "0.9rem", color: "#333" }}>
                {selectedFeedback?.email}
              </Typography>
            </Box>

            <Box>
              <Typography sx={{ fontSize: "0.8rem", color: "#999", fontWeight: 600 }}>
                PHONE
              </Typography>
              <Typography sx={{ fontSize: "0.9rem", color: "#333" }}>
                {selectedFeedback?.phone || "—"}
              </Typography>
            </Box>

            <Box>
              <Typography sx={{ fontSize: "0.8rem", color: "#999", fontWeight: 600 }}>
                CATEGORY
              </Typography>
              <Chip
                label={getCategoryLabel(selectedFeedback?.category)}
                size="small"
                sx={{
                  backgroundColor: getCategoryColor(selectedFeedback?.category),
                  color: "#fff",
                  fontWeight: 600,
                  fontSize: "0.75rem",
                  mt: 0.5
                }}
              />
            </Box>

            <Box>
              <Typography sx={{ fontSize: "0.8rem", color: "#999", fontWeight: 600 }}>
                MESSAGE
              </Typography>
              <Typography
                sx={{
                  fontSize: "0.9rem",
                  color: "#333",
                  backgroundColor: "#f9f9f9",
                  p: 1.5,
                  borderRadius: 1,
                  mt: 0.5,
                  lineHeight: 1.6
                }}
              >
                {selectedFeedback?.message}
              </Typography>
            </Box>

            <Box>
              <Typography sx={{ fontSize: "0.8rem", color: "#999", fontWeight: 600 }}>
                DATE SUBMITTED
              </Typography>
              <Typography sx={{ fontSize: "0.9rem", color: "#333" }}>
                {new Date(selectedFeedback?.createdAt).toLocaleString()}
              </Typography>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button
            onClick={handleCloseDialog}
            sx={{ fontSize: "0.9rem", textTransform: "none" }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AdminFeedback;
